import { Injectable, signal } from '@angular/core';
import { AvailableLanguagesEnum } from '../constants/available-languages.enum';

/** Reads persisted credentials, tolerating absent or corrupt storage so a bad value can't brick bootstrap. */
const readStoredCredentials = (): Credentials | null => {
  try {
    return JSON.parse(sessionStorage.getItem('credentials') ?? 'null');
  } catch {
    return null;
  }
};

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  readonly noAuthEndPoint = [
    '/api/auth/admin/login',
    '/api/auth/super-admin/login',
    '/api/auth/model-admin/login',
    '/api/auth/request-reset-password',
    '/api/auth/reset-password',
  ];
  readonly excludedPayloads = ['/api/brand', '/api/model'];

  private readonly credentialsState = signal<Credentials | null>(readStoredCredentials());

  /** Current credentials as a reactive signal. */
  readonly credentialsSignal = this.credentialsState.asReadonly();

  /** Current credentials snapshot, kept as a property-style accessor for imperative call sites. */
  get credentials(): Credentials | null {
    return this.credentialsState();
  }

  setCredentials(credentials: Credentials | null) {
    sessionStorage.setItem('credentials', JSON.stringify(credentials));
    this.credentialsState.set(credentials);
  }

  clearCredentials() {
    this.setCredentials(null);
  }
}

export type Credentials = {
  payload: {
    role: 'SUPER_ADMIN' | 'ADMIN' | 'MODEL_ADMIN' | 'USER';
    id: number;
    language?: AvailableLanguagesEnum;
  };
  token: string;
  refreshToken: string;
};
